import { Option } from '@inrev/core';
import { DateTime } from 'luxon';
import { PeriodType } from '../enums.mjs';
import { Currency } from '../types/currency/currency.mjs';

const DAYS_PER_YEAR = 365;
const balanceSheetLineValuesFromAllowed = (allowed) => {
    const { current: currentAssets } = allowed.assets;
    const { current: currentLiabilities, nonCurrent } = allowed.liabilities;
    return {
        cash: currentAssets.cash.total,
        marketableSecurities: currentAssets.marketableSecurities.total,
        accountsReceivable: currentAssets.accountsReceivable.total,
        underbillings: currentAssets.underbillings.total,
        currentAssets: currentAssets.total,
        totalAssets: allowed.assets.total,
        accountsPayable: currentLiabilities.accountsPayable.total,
        creditCardsBalance: currentLiabilities.creditCardsBalance.total,
        cashOverdraft: currentLiabilities.cashOverdraft.total,
        blocBalance: currentLiabilities.blocBalance.total,
        currentPortionOfLtd: currentLiabilities.currentPortionOfLtd.total,
        otherNotesPayable: currentLiabilities.otherNotesPayable.total,
        currentLiabilities: currentLiabilities.total,
        ltdBankTermLoan: nonCurrent.ltdBankTermLoan.total,
        ltdRealEstate: nonCurrent.ltdRealEstate.total,
        ltdEquipment: nonCurrent.ltdEquipment.total,
        ltdOther: nonCurrent.ltdOther.total,
        equity: allowed.equity.total,
    };
};
const incomeStatementLineValuesFromAllowed = (allowed) => ({
    revenue: allowed.revenue.total,
    grossProfit: allowed.grossProfit ?? null,
    operatingExpenses: allowed.operatingExpenses,
    operatingProfit: allowed.operatingProfit ?? null,
    netProfit: allowed.netProfit ?? null,
    depreciationAndAmortization: allowed.depreciationAndAmortization,
    interestExpense: allowed.interestExpense,
    taxes: allowed.taxes,
});
const MONTHS_BY_PERIOD_TYPE = {
    [PeriodType.Fye]: 12,
    [PeriodType.Ttm]: 12,
    [PeriodType.Qtd]: 3,
    [PeriodType.Ytd]: null,
    [PeriodType.Other]: null,
};
const computeAnnualizationFactor = (period) => {
    if (period.periodStartDate) {
        const start = DateTime.fromISO(period.periodStartDate, { zone: "utc" });
        const end = DateTime.fromISO(period.periodEndDate, { zone: "utc" });
        const days = Math.round(end.diff(start, "days").days) + 1;
        return Number.isFinite(days) && days > 0 ? DAYS_PER_YEAR / days : 1;
    }
    const months = MONTHS_BY_PERIOD_TYPE[period.periodType] ??
        (period.periodType === PeriodType.Ytd ? DateTime.fromISO(period.periodEndDate).month : null);
    return months && months > 0 ? 12 / months : 1;
};
const annualizeFlow = (value, annualizationFactor) => value === null ? null : value * annualizationFactor;
const sumMoney = (...values) => Currency.sum(values.map((v) => Option.getOrElse(v, 0)));
const ratio = (numerator, denominator) => {
    if (denominator === null || denominator === 0)
        return null;
    return numerator / denominator;
};
const percentOf = (numerator, denominator) => {
    const value = ratio(numerator, denominator);
    return value === null ? null : value * 100;
};
const computeCashAndEquivalents = (bs) => sumMoney(bs.cash, bs.marketableSecurities);
const computeBalanceSheetWorkingCapital = (bs) => Currency.subtract(bs.currentAssets ?? 0, bs.currentLiabilities ?? 0);
const computeWorkingCapitalToTotalAssetsRatio = (bs) => ratio(computeBalanceSheetWorkingCapital(bs), bs.totalAssets);
const computeWorkingCapitalToCurrentAssetsRatio = (bs) => ratio(computeBalanceSheetWorkingCapital(bs), bs.currentAssets);
const computeUnderbillingsToWorkingCapitalRatio = (bs) => ratio(bs.underbillings ?? 0, computeBalanceSheetWorkingCapital(bs));
const computeBlocAvailable = (bs, blocSize) => (blocSize === null ? null : Currency.subtract(blocSize, bs.blocBalance ?? 0));
const computeTotalAvailableLiquidity = (bs, blocSize) => Currency.add(computeCashAndEquivalents(bs), computeBlocAvailable(bs, blocSize) ?? 0);
const computeLongTermDebt = (bs) => sumMoney(bs.ltdBankTermLoan, bs.ltdRealEstate, bs.ltdEquipment, bs.ltdOther);
const computeCurrentDebt = (bs) => sumMoney(bs.creditCardsBalance, bs.cashOverdraft, bs.blocBalance, bs.currentPortionOfLtd, bs.otherNotesPayable);
const computeCurrentDebtToTotalAssetsRatio = (bs) => ratio(computeCurrentDebt(bs), bs.totalAssets);
const computeTotalDebt = (bs) => Currency.add(computeCurrentDebt(bs), computeLongTermDebt(bs));
const computeEbitda = (is) => sumMoney(is.operatingProfit, is.depreciationAndAmortization, is.interestExpense, is.taxes);
const computeCurrentRatio = (bs) => ratio(bs.currentAssets ?? 0, bs.currentLiabilities);
const computeArToApRatio = (bs) => ratio(bs.accountsReceivable ?? 0, bs.accountsPayable);
const computeArToWorkingCapitalRatio = (bs) => ratio(bs.accountsReceivable ?? 0, computeBalanceSheetWorkingCapital(bs));
const computeDebtToEquityRatio = (bs) => ratio(computeTotalDebt(bs), bs.equity);
const computeDebtToEbitdaRatio = (bs, is) => ratio(computeTotalDebt(bs), computeEbitda(is));
const computeArToEquityRatio = (bs) => ratio(bs.accountsReceivable ?? 0, bs.equity);
const computeAnnualizedRevenueGrowth = (current, prior) => {
    const revenue = annualizeFlow(current.incomeStatement.revenue ?? 0, current.annualizationFactor) ?? 0;
    const priorRevenue = annualizeFlow(prior.incomeStatement.revenue, prior.annualizationFactor);
    return percentOf(Currency.subtract(revenue, priorRevenue ?? 0), priorRevenue);
};
const computeGrossMarginPercent = (is) => percentOf(is.grossProfit ?? 0, is.revenue);
const computeGAndAPercentOfRevenue = (is) => percentOf(is.operatingExpenses ?? 0, is.revenue);
const computeOperatingMarginPercent = (is) => percentOf(is.operatingProfit ?? 0, is.revenue);
const computeNetMarginPercent = (is) => percentOf(is.netProfit ?? 0, is.revenue);
const resolvePeriodProfitability = (period) => !period || period.isManual
    ? { kind: "binary", profitable: period?.profitable ?? null }
    : { kind: "netProfit", netProfit: period.netProfit ?? null };

export { annualizeFlow, balanceSheetLineValuesFromAllowed, computeAnnualizationFactor, computeAnnualizedRevenueGrowth, computeArToApRatio, computeArToEquityRatio, computeArToWorkingCapitalRatio, computeBalanceSheetWorkingCapital, computeBlocAvailable, computeCashAndEquivalents, computeCurrentDebt, computeCurrentDebtToTotalAssetsRatio, computeCurrentRatio, computeDebtToEbitdaRatio, computeDebtToEquityRatio, computeEbitda, computeGAndAPercentOfRevenue, computeGrossMarginPercent, computeLongTermDebt, computeNetMarginPercent, computeOperatingMarginPercent, computeTotalAvailableLiquidity, computeTotalDebt, computeUnderbillingsToWorkingCapitalRatio, computeWorkingCapitalToCurrentAssetsRatio, computeWorkingCapitalToTotalAssetsRatio, incomeStatementLineValuesFromAllowed, resolvePeriodProfitability };
